import React, { useState, useRef, useCallback } from 'react';
import { Box, Card, CardContent, Typography, CircularProgress, List, Divider } from '@mui/material';
import { useDragDropManager } from 'react-dnd';
import { useBudget } from '../../contexts/BudgetContext';
import { useTransactions } from '../../contexts/TransactionContext';
import { useLoading } from '../../contexts/LoadingContext';
import { DroppableCategory } from './DroppableCategory';
import { alpha } from '@mui/material/styles';
import { Budget } from '../../types';
import { budgets } from '../../services/api';

const BudgetCategories: React.FC = () => {
  const { 
    currentBudget,
    handleMoveBudgetCategory,
    formatCurrency,
    setCurrentBudget
  } = useBudget();

  console.log('BudgetCategories: currentBudget:', currentBudget);
  console.log('BudgetCategories: currentBudget exists:', !!currentBudget);
  console.log('BudgetCategories: currentBudget categories:', currentBudget?.categories?.length || 0);

  const { handleUpdateTransactionCategory, refreshTransactions } = useTransactions();
  const { isLoading } = useLoading();
  const [dragValue, setDragValue] = useState<boolean>(false);
  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();
  const timerRef = useRef<NodeJS.Timer>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate totals and progress
  const totals = React.useMemo(() => {
    if (!currentBudget) return { 
      income: 0, expenses: 0, savings: 0,
      expenseProgress: 0, savingsProgress: 0,
      expenseBudgeted: 0, savingsBudgeted: 0
    };
    
    const result = currentBudget.categories.reduce((acc, cat) => {
      if (cat.type === 'income') {
        acc.income += cat.actualAmount;
      } else if (cat.type === 'expense') {
        acc.expenses += cat.actualAmount;
        acc.expenseBudgeted += cat.budgetedAmount;
      } else if (cat.type === 'savings') {
        acc.savings += cat.actualAmount;
        acc.savingsBudgeted += cat.budgetedAmount;
      }
      return acc;
    }, { 
      income: 0, expenses: 0, savings: 0,
      expenseBudgeted: 0, savingsBudgeted: 0
    });

    // Calculate progress percentages
    const expenseProgress = result.expenseBudgeted > 0 ? (result.expenses / result.expenseBudgeted) * 100 : 0;
    
    // For savings: negative when under goal, positive when over goal
    const savingsProgress = result.savingsBudgeted > 0 
      ? ((result.savings - result.savingsBudgeted) / result.savingsBudgeted) * 100
      : 0;

    return {
      ...result,
      expenseProgress,
      savingsProgress
    };
  }, [currentBudget]);

  const setScrollInterval = useCallback((speed: number, container: HTMLElement) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    timerRef.current = setInterval(() => {
      if (!monitor.isDragging()) {
        clearInterval(timerRef.current);
        return;
      }
      container.scrollBy(0, speed);
    }, 16); // Adjust to 60fps for smoother scrolling
  }, [monitor]);

  React.useEffect(() => {
    if (dragValue && containerRef.current) {
      const unsubscribe = monitor.subscribeToOffsetChange(() => {
        const offset = monitor.getClientOffset();
        const container = containerRef.current;
        if (!offset || !container) return;

        const containerRect = container.getBoundingClientRect();
        const scrollThreshold = 150; // Increased threshold for earlier scroll trigger
        const maxScrollSpeed = 15; // Maximum scroll speed

        // Calculate relative position within threshold
        const topDelta = offset.y - (containerRect.top + scrollThreshold);
        const bottomDelta = offset.y - (containerRect.bottom - scrollThreshold);

        // Calculate scroll speed based on position
        if (topDelta < 0) {
          const speed = Math.max(-maxScrollSpeed, (topDelta / scrollThreshold) * maxScrollSpeed);
          setScrollInterval(speed, container);
        } else if (bottomDelta > 0) {
          const speed = Math.min(maxScrollSpeed, (bottomDelta / scrollThreshold) * maxScrollSpeed);
          setScrollInterval(speed, container);
        } else {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
        }
      });

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        unsubscribe();
      };
    }
  }, [dragValue, monitor, setScrollInterval]);

  React.useEffect(() => {
    const unsubscribe = monitor.subscribeToStateChange(() => {
      setDragValue(monitor.isDragging());
    });

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      unsubscribe();
    };
  }, [monitor]);

  const handleDeleteCategory = async (category: Budget['categories'][0]) => {
    if (!currentBudget) return;

    const updatedCategories = currentBudget.categories.filter(
      cat => !(cat.name === category.name && cat.type === category.type)
    );

    const totalBudgeted = updatedCategories
      .filter(cat => cat.type === 'expense' || cat.type === 'savings')
      .reduce((sum, cat) => sum + cat.budgetedAmount, 0);

    try {
      await budgets.update(currentBudget._id, {
        categories: updatedCategories,
        totalBudgeted
      });

      setCurrentBudget(prev => prev ? {
        ...prev,
        categories: updatedCategories,
        totalBudgeted
      } : null);

      // Refresh transactions to reflect deleted transactions
      await refreshTransactions();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const incomeCategoriesComponent = React.useMemo(() => {
    if (!currentBudget) return null;
    return currentBudget.categories
      .filter(cat => cat.type === 'income')
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((category, index) => (
        <DroppableCategory
          key={category.name}
          category={category}
          index={index}
          onDrop={(transaction) => handleUpdateTransactionCategory(transaction, category.name)}
          onMove={handleMoveBudgetCategory}
          onDelete={handleDeleteCategory}
        />
      ));
  }, [currentBudget, handleUpdateTransactionCategory, handleMoveBudgetCategory, handleDeleteCategory]);

  const expenseCategoriesComponent = React.useMemo(() => {
    if (!currentBudget) return null;
    return currentBudget.categories
      .filter(cat => cat.type === 'expense')
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((category, index) => (
        <DroppableCategory
          key={category.name}
          category={category}
          index={index}
          onDrop={(transaction) => handleUpdateTransactionCategory(transaction, category.name)}
          onMove={handleMoveBudgetCategory}
          onDelete={handleDeleteCategory}
        />
      ));
  }, [currentBudget, handleUpdateTransactionCategory, handleMoveBudgetCategory, handleDeleteCategory]);

  const savingsCategoriesComponent = React.useMemo(() => {
    if (!currentBudget) return null;
    return currentBudget.categories
      .filter(cat => cat.type === 'savings')
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((category, index) => (
        <DroppableCategory
          key={category.name}
          category={category}
          index={index}
          onDrop={(transaction) => handleUpdateTransactionCategory(transaction, category.name)}
          onMove={handleMoveBudgetCategory}
          onDelete={handleDeleteCategory}
        />
      ));
  }, [currentBudget, handleUpdateTransactionCategory, handleMoveBudgetCategory, handleDeleteCategory]);

  const content = React.useMemo(() => {
    if (isLoading) {
      return (
        <Card sx={{ 
          height: '100%',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: theme => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            borderRadius: '16px 16px 0 0',
            zIndex: 1,
          }
        }}>
          <CardContent>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ 
        height: '100%',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: theme => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          borderRadius: '16px 16px 0 0',
          zIndex: 1,
        }
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {currentBudget ? (
            <Box 
              ref={containerRef}
              sx={{ 
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                minHeight: 'fit-content',
                overflow: 'visible',
              }}
            >
              <Box mb={2}>
                <Typography variant="h6" gutterBottom>
                  Income Categories
                </Typography>
                <Typography variant="subtitle1" color="primary.main" sx={{ ml: 2 }}>
                  Total: {formatCurrency(totals.income)}
                </Typography>
              </Box>
              <List sx={{ pt: 0, pb: 0 }}>
                {incomeCategoriesComponent}
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Box mb={2}>
                <Typography variant="h6" gutterBottom>
                  Savings Categories
                </Typography>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle1" color="success.main">
                    {formatCurrency(totals.savings)} of {formatCurrency(totals.savingsBudgeted)}
                  </Typography>
                  {totals.savingsBudgeted > 0 && (
                    <Typography 
                      variant="caption" 
                      color={totals.savingsProgress >= 0 ? "success.main" : "text.secondary"}
                    >
                      {totals.savingsProgress >= 0 
                        ? `${Math.round(totals.savingsProgress)}% above goals` 
                        : `${Math.round(Math.abs(totals.savingsProgress))}% below goals`
                      }
                    </Typography>
                  )}
                </Box>
              </Box>
              <List sx={{ pt: 0, pb: 0 }}>
                {savingsCategoriesComponent}
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Box mb={2}>
                <Typography variant="h6" gutterBottom>
                  Expense Categories
                </Typography>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle1" color="error">
                    {formatCurrency(totals.expenses)} of {formatCurrency(totals.expenseBudgeted)}
                  </Typography>
                  {totals.expenseBudgeted > 0 && (
                    <Typography 
                      variant="caption" 
                      color={totals.expenseProgress > 100 ? "error.main" : "text.secondary"}
                    >
                      {Math.round(totals.expenseProgress)}% of budget used
                      {totals.expenseProgress > 100 && " (over budget)"}
                    </Typography>
                  )}
                </Box>
              </Box>
              <List sx={{ pt: 0, pb: 0 }}>
                {expenseCategoriesComponent}
              </List>
            </Box>
          ) : (
            <Typography color="textSecondary">No budget set for this month</Typography>
          )}
        </CardContent>
      </Card>
    );
  }, [isLoading, currentBudget, incomeCategoriesComponent, expenseCategoriesComponent, savingsCategoriesComponent, totals, formatCurrency]);

  return content;
};

export default BudgetCategories; 